import { cva } from 'class-variance-authority';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';

import { cn } from '../lib/cn';
import { Button } from '../ui/Button';
import { ActionButton } from '../ui/kit';
import { selectTour } from '../store/selectors/tour';
import { selectUser } from '../store/selectors/auth';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { tourEnded, completeWalkthroughAction } from '../store/slices/tour';
import {
  stepsFor,
  placeCard,
  tourSurface,
  onTourScreen,
  findTourTarget,
  isTourTargetShown,
  waitForTourTarget,
} from '../lib/tour';

// Covers a lazy route chunk plus its first data read; past this the step is passed over.
const TARGET_WAIT_MS = 3000;
const SPOTLIGHT_PAD = 6;

// The scrim is the spotlight's shadow, so the cut-out is the element itself.
const spotlightVariants = cva(
  'fixed left-0 top-0 z-50 rounded-xl motion-safe:transition-transform motion-safe:duration-normal motion-safe:ease-enter',
  {
    variants: {
      surface: {
        admin: 'shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] ring-1 ring-ring',
        console: 'shadow-[0_0_0_9999px_rgba(5,8,9,0.8)] ring-1 ring-dusq-accent/40',
      },
    },
  }
);

const cardVariants = cva(
  'fixed left-0 top-0 z-50 flex w-[min(320px,calc(100vw-32px))] flex-col gap-3 p-4 outline-none shadow-lg motion-safe:animate-fade-in motion-safe:transition-transform motion-safe:duration-normal motion-safe:ease-enter',
  {
    variants: {
      surface: {
        admin: 'rounded-2xl border border-border bg-popover text-popover-foreground',
        console: 'rounded-[16px] border border-dusq-border bg-dusq-raised font-dusq text-dusq-text',
      },
    },
  }
);

const ARROW_EDGE = {
  bottom: '-top-1.5 border-l border-t',
  top: '-bottom-1.5 border-b border-r',
  right: '-left-1.5 border-b border-l',
  left: '-right-1.5 border-r border-t',
};

const TourButton = ({ surface, primary, className, ...props }) =>
  surface === 'console' ? (
    <ActionButton tone={primary ? 'solid' : 'neutral'} className={className} {...props} />
  ) : (
    <Button variant={primary ? 'primary' : 'outlined'} className={className} {...props} />
  );

const sameFrame = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const Tour = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector(selectUser);
  const tour = useAppSelector(selectTour);
  const running = tour.status === 'running';
  const steps = useMemo(() => stepsFor(user), [user]);
  const surface = tourSurface(pathname);

  // Indices of the steps shown so far; the last one is on screen.
  const [history, setHistory] = useState([]);
  const [target, setTarget] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [frame, setFrame] = useState(null);
  // Radix portals the card a render after the step is chosen, so its node is state too.
  // Until it is placed it is transparent, not hidden: a hidden element cannot take focus.
  const [cardNode, setCardNode] = useState(null);
  const cardRef = useRef(null);
  const attachCard = useCallback((node) => {
    cardRef.current = node;
    setCardNode(node);
  }, []);
  const pendingRef = useRef(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const current = history.length ? history[history.length - 1] : -1;
  const step = steps[current];

  // Steps past `index` that are on another screen, or on this one with a target to point at.
  const availableAfter = useCallback(
    (index) =>
      steps
        .map((candidate, i) => i)
        .filter(
          (i) =>
            i > index &&
            (!onTourScreen(steps[i], pathnameRef.current) || findTourTarget(steps[i].id))
        ),
    [steps]
  );

  // Unset before cancelling, so the awaiting goTo sees its wait was abandoned and stops.
  const cancelPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.cancel();
  }, []);

  // Steps shown this run; a run that never showed one has nothing to record as seen.
  const shownRef = useRef(0);

  const finish = useCallback(() => {
    cancelPending();
    // A cancelled wait never reaches the code that clears this, and a failed save keeps the card.
    setResolving(false);
    if (tour.mode === 'first' && shownRef.current > 0) dispatch(completeWalkthroughAction());
    else dispatch(tourEnded());
  }, [cancelPending, dispatch, tour.mode]);

  // Show the step at `index`: use its target if present, else go to its screen and wait.
  const goTo = useCallback(
    async (index, nextHistory, forward) => {
      cancelPending();
      setResolving(true);
      let element = findTourTarget(steps[index].id);
      if (!element) {
        if (!onTourScreen(steps[index], pathnameRef.current)) navigate(steps[index].path);
        const wait = waitForTourTarget(steps[index].id, TARGET_WAIT_MS);
        pendingRef.current = wait;
        element = await wait.promise;
        if (pendingRef.current !== wait) return;
        pendingRef.current = null;
      }
      if (element) {
        element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        shownRef.current += 1;
        setTarget(element);
        setHistory(nextHistory);
        setResolving(false);
        return;
      }
      // Nothing to point at on that screen: pass over it in the direction of travel.
      if (forward) {
        const [next] = availableAfter(index);
        if (next === undefined) finish();
        else goTo(next, [...nextHistory.slice(0, -1), next], true);
      } else if (nextHistory.length > 1) {
        goTo(nextHistory[nextHistory.length - 2], nextHistory.slice(0, -1), false);
      } else {
        setResolving(false);
      }
    },
    [steps, navigate, availableAfter, finish, cancelPending]
  );

  const next = useCallback(() => {
    if (resolving || tour.saving) return;
    const [index] = availableAfter(current);
    if (index === undefined) finish();
    else goTo(index, [...history, index], true);
  }, [resolving, tour.saving, availableAfter, current, finish, goTo, history]);

  const back = useCallback(() => {
    if (resolving || tour.saving || history.length < 2) return;
    goTo(history[history.length - 2], history.slice(0, -1), false);
  }, [resolving, tour.saving, history, goTo]);

  // Kept in a ref so only a start or stop runs the effect, not every step's new closures.
  const startRef = useRef(null);
  startRef.current = async () => {
    shownRef.current = 0;
    // A lazy page can mount after the profile lands, so give this screen's first target time.
    const here = steps.find((candidate) => onTourScreen(candidate, pathnameRef.current));
    if (here && !findTourTarget(here.id)) {
      const wait = waitForTourTarget(here.id, TARGET_WAIT_MS);
      pendingRef.current = wait;
      await wait.promise;
      if (pendingRef.current !== wait) return;
      pendingRef.current = null;
    }
    const [first] = availableAfter(-1);
    if (first === undefined) finish();
    else goTo(first, [first], true);
  };

  // Start from the first reachable step; a stop clears everything for the next run.
  useEffect(() => {
    if (!running) {
      cancelPending();
      setHistory([]);
      setTarget(null);
      setFrame(null);
      setResolving(false);
      return undefined;
    }
    startRef.current();
    return cancelPending;
  }, [running, cancelPending]);

  // Measure the target and the card; the spotlight and the card follow on every change.
  const layout = useCallback(() => {
    if (!cardNode) return;
    // A breakpoint change can move the target offscreen; its on-screen alternate takes over.
    if (target && step && !isTourTargetShown(target)) {
      const shown = findTourTarget(step.id);
      if (shown) {
        setTarget(shown);
        return;
      }
    }
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const box = target?.isConnected ? target.getBoundingClientRect() : null;
    const spot = box
      ? {
          top: box.top - SPOTLIGHT_PAD,
          left: box.left - SPOTLIGHT_PAD,
          width: box.width + SPOTLIGHT_PAD * 2,
          height: box.height + SPOTLIGHT_PAD * 2,
        }
      : null;
    const size = { width: cardNode.offsetWidth, height: cardNode.offsetHeight };
    const nextFrame = {
      spot,
      card: placeCard(spot, size, viewport),
      remaining: availableAfter(current).length,
    };
    setFrame((previous) => (sameFrame(previous, nextFrame) ? previous : nextFrame));
  }, [cardNode, target, step, availableAfter, current]);

  useLayoutEffect(() => {
    if (!running || !cardNode) return undefined;
    layout();
    window.addEventListener('resize', layout);
    window.addEventListener('scroll', layout, true);
    // Drawers slide off after the resize event fires; transitionend catches where they settle.
    window.addEventListener('transitionend', layout, true);
    const sizes = new ResizeObserver(layout);
    sizes.observe(cardNode);
    if (target) sizes.observe(target);
    // A re-render can swap the target's node; the same id names its replacement.
    const mutations = new MutationObserver(() => {
      if (target && !target.isConnected) setTarget(findTourTarget(step.id));
      else layout();
    });
    mutations.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener('resize', layout);
      window.removeEventListener('scroll', layout, true);
      window.removeEventListener('transitionend', layout, true);
      sizes.disconnect();
      mutations.disconnect();
    };
  }, [running, cardNode, layout, target, step]);

  const onKeyDown = (event) => {
    // A focused button keeps Enter for itself; the card itself treats it as Next.
    const onCard = event.target === event.currentTarget;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        next();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        back();
        break;
      case 'Enter':
        if (onCard) {
          event.preventDefault();
          next();
        }
        break;
      default:
    }
  };

  if (!running || !step) return null;

  const last = frame ? frame.remaining === 0 : false;
  const position = history.length;
  const total = position + (frame ? frame.remaining : 0);
  const busy = resolving || tour.saving;
  const spot = frame?.spot || {
    top: window.innerHeight / 2,
    left: window.innerWidth / 2,
    width: 0,
    height: 0,
  };
  const card = frame?.card;
  // Only the position moves (transform); the size snaps, per the motion rule.
  const spotStyle = {
    width: spot.width,
    height: spot.height,
    transform: `translate(${spot.left}px, ${spot.top}px)`,
  };

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-tour-spot={step.id}
          className={spotlightVariants({ surface })}
          style={spotStyle}
        />
        <DialogPrimitive.Content
          ref={attachCard}
          tabIndex={-1}
          data-tour-card={step.id}
          onKeyDown={onKeyDown}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cardRef.current?.focus();
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            finish();
          }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
          className={cn(cardVariants({ surface }), !card && 'opacity-0')}
          style={card ? { transform: `translate(${card.left}px, ${card.top}px)` } : undefined}
        >
          {card && card.side !== 'center' && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute h-3 w-3 rotate-45 border-inherit bg-inherit',
                ARROW_EDGE[card.side]
              )}
              style={
                card.side === 'top' || card.side === 'bottom'
                  ? { left: card.arrow - 6 }
                  : { top: card.arrow - 6 }
              }
            />
          )}
          <div aria-live="polite" aria-atomic="true" className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <DialogPrimitive.Title className="text-balance text-md font-medium">
                {step.title}
              </DialogPrimitive.Title>
              <span
                className={cn(
                  'shrink-0 text-xs tabular-nums',
                  surface === 'console' ? 'text-dusq-grey400' : 'text-gray-dark'
                )}
              >
                {position} of {total}
              </span>
            </div>
            <DialogPrimitive.Description
              className={cn(
                'text-pretty text-base',
                surface === 'console' ? 'text-dusq-emphasis-mid' : 'text-gray-dark'
              )}
            >
              {step.body}
            </DialogPrimitive.Description>
          </div>
          {tour.error && (
            <p
              role="alert"
              className={cn(
                'text-sm',
                surface === 'console' ? 'text-dusq-warning' : 'text-danger-foreground'
              )}
            >
              {tour.error}
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            {last ? (
              <span />
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={busy}
                className={cn(
                  'rounded px-1 text-sm underline-offset-4 hover:underline disabled:opacity-50',
                  surface === 'console' ? 'text-dusq-grey400' : 'text-gray-dark'
                )}
              >
                Skip the tour
              </button>
            )}
            <div className="flex items-center gap-2">
              <TourButton surface={surface} onClick={back} disabled={busy || history.length < 2}>
                Back
              </TourButton>
              <TourButton surface={surface} primary onClick={last ? finish : next} disabled={busy}>
                {last ? 'Done' : 'Next'}
              </TourButton>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
